import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../../components/dashboardlayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ArrowLeft, Send, X, Plus, Loader2, ChevronLeft, ChevronRight, Search, Copy, Check, Video, FileText, MessageSquare, Upload, Download } from 'lucide-react';
import { useRbac } from '../../context';
import axiosInstance from '../../services/apiinterseptor';
import GaplessWebMPlayer from "../../components/VideoPlayer"

interface Candidate {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

interface CandidateErrors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
}

interface InterviewInvite {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
  sessionId: string;
  invitedAt: string;
  expiresAt: string;
  invitedBy:{
    _id:string,
    username:string,
    email:string
  }
  jobId: {
    _id: string;
    title: string;
    brandName?: string;
  };
}

interface InvitesResponse {
  status: string;
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
  results: number;
  data: {
    invites: InterviewInvite[];
  };
}

const AIInterview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const jobId = location.state?.jobId;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([
    { firstName: '', lastName: '', phone: '', email: '' }
  ]);
  const [errors, setErrors] = useState<CandidateErrors[]>([]);
  const [submitSuccess, setSubmitSuccess] = useState<string>('');
  const [submitError, setSubmitError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [invites, setInvites] = useState<InterviewInvite[]>([]);
  const [isLoadingInvites, setIsLoadingInvites] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isResultDialogOpen, setIsResultDialogOpen] = useState<boolean>(false);
  const [selectedInvite, setSelectedInvite] = useState<InterviewInvite | null>(null);
  const [interviewResult, setInterviewResult] = useState<any>(null);
  const [isLoadingResult, setIsLoadingResult] = useState<boolean>(false);
  const [sequencialVideoUrl, setSequencialVideoUrl] = useState([]);
  const [isCsvDialogOpen, setIsCsvDialogOpen] = useState<boolean>(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvError, setCsvError] = useState<string>('');
  const [csvParsedData, setCsvParsedData] = useState<Candidate[]>([]);
  const [isParsingCsv, setIsParsingCsv] = useState<boolean>(false);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isDownloadingSample, setIsDownloadingSample] = useState<boolean>(false);
  const [isResendDialogOpen, setIsResendDialogOpen] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const {user} = useRbac()
  const handleAddCandidate = () => {
    if (candidates.length < 5) {
      setCandidates([...candidates, { firstName: '', lastName: '', phone: '', email: '' }]);
      setErrors([...errors, {}]);
      setSubmitSuccess('');
    }
  };

  const handleRemoveCandidate = (index: number) => {
    if (candidates.length > 1) {
      setCandidates(candidates.filter((_, i) => i !== index));
      setErrors(errors.filter((_, i) => i !== index));
      setSubmitSuccess('');
    }
  };

  const handleCandidateChange = (index: number, field: keyof Candidate, value: string) => {
    const updatedCandidates = [...candidates];
    updatedCandidates[index][field] = value;
    setCandidates(updatedCandidates);

    // Clear error for this field when user types
    if (errors[index] && errors[index][field]) {
      const updatedErrors = [...errors];
      updatedErrors[index] = { ...updatedErrors[index], [field]: undefined };
      setErrors(updatedErrors);
    }
    setSubmitSuccess('');
    setSubmitError('');
  };

  const validateForm = (): boolean => {
    const newErrors: CandidateErrors[] = [];
    let hasErrors = false;

    // First pass: validate required fields and email format
    for (let i = 0; i < candidates.length; i++) {
      const candidate = candidates[i];
      const candidateErrors: CandidateErrors = {};

      if (!candidate.firstName.trim()) {
        candidateErrors.firstName = 'First name is required';
        hasErrors = true;
      }
      if (!candidate.lastName.trim()) {
        candidateErrors.lastName = 'Last name is required';
        hasErrors = true;
      }
      if (!candidate.phone.trim()) {
        candidateErrors.phone = 'Phone is required';
        hasErrors = true;
      }else{
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!phoneRegex.test(candidate.phone)) {
          candidateErrors.phone = 'Please enter a valid 10-digit phone number';
          hasErrors = true;
        }
      }
      if (!candidate.email.trim()) {
        candidateErrors.email = 'Email is required';
        hasErrors = true;
      } else {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(candidate.email)) {
          candidateErrors.email = 'Please enter a valid email address';
          hasErrors = true;
        }
      }

      newErrors.push(candidateErrors);
    }

    // Second pass: check for duplicate phone numbers
    const phoneCounts = new Map<string, number[]>();
    
    candidates.forEach((candidate, index) => {
      const phone = candidate.phone.trim();
      if (phone) {
        if (!phoneCounts.has(phone)) {
          phoneCounts.set(phone, []);
        }
        phoneCounts.get(phone)!.push(index);
      }
    });

    // Mark duplicate phone numbers with errors
    phoneCounts.forEach((indices, phone) => {
      if (indices.length > 1) {
        indices.forEach(index => {
          if (!newErrors[index]) {
            newErrors[index] = {};
          }
          const otherRows = indices.filter(i => i !== index).map(i => i + 1).join(', ');
          newErrors[index].phone = `Phone number must be unique. Duplicate found in row${indices.length > 2 ? 's' : ''} ${otherRows}`;
          hasErrors = true;
        });
      }
    });

    setErrors(newErrors);

    if (hasErrors) {
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitSuccess('');

    try {
      const response = await axiosInstance.post('/ai-interview/invite-candidates', {
        jobId: jobId,
        candidates: candidates.map(candidate => ({
          firstName: candidate.firstName.trim(),
          lastName: candidate.lastName.trim(),
          phone: candidate.phone.trim(),
          email: candidate.email.trim(),
        }))
      });

      if (response.data && response.status === 200 || response.status === 201) {
        setSubmitSuccess(`Successfully invited ${candidates.length} candidate(s)`);
        setTimeout(() => {
          setIsDialogOpen(false);
          setCandidates([{ firstName: '', lastName: '', phone: '', email: '' }]);
          setErrors([]);
          setSubmitSuccess('');
          // Refresh invites list
          fetchInvites();
        }, 1500);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to invite candidates. Please try again.';
      setSubmitSuccess('');
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setCandidates([{ firstName: '', lastName: '', phone: '', email: '' }]);
    setErrors([]);
    setSubmitSuccess('');
    setSubmitError('');
    setIsSubmitting(false);
  };

  const fetchInvites = useCallback(async () => {
    setIsLoadingInvites(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });

      if (jobId) {
        params.append('jobId', jobId);
      }

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await axiosInstance.get<InvitesResponse>(`/ai-interview/invites?${params.toString()}`);

      if (response.data && response.data.status === 'success') {
        setInvites(response.data.data.invites || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalResults(response.data.totalResults || 0);
      }
    } catch (error: any) {
      console.error('Failed to fetch invites:', error);
      setInvites([]);
    } finally {
      setIsLoadingInvites(false);
    }
  }, [currentPage, itemsPerPage, jobId, searchTerm, statusFilter]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  // Reset to page 1 when search or status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Refresh invites after successful submission
  useEffect(() => {
    if (submitSuccess) {
      const timer = setTimeout(() => {
        fetchInvites();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [submitSuccess, fetchInvites]);

  // Fetch interview result when dialog opens
  useEffect(() => {
    if (isResultDialogOpen && selectedInvite && !interviewResult) {
      const fetchInterviewResult = async () => {
        setIsLoadingResult(true);
        try {
          const response = await axiosInstance.get(`/ai-interview/report/${selectedInvite.sessionId}`);
          if (response.data && response.data.status === 'success') {
            console.log("show result aapi is success vedio",response.data.data.sequencialVideoUrl?.map((video: any) => ({ url: video.url, index: video.index })));
            setInterviewResult(response.data.data);
            setSequencialVideoUrl(response.data.data.sequencialVideoUrl?.map((video: any) => ({ url: video.url, index: video.index })));
          }
        } catch (error: any) {
          console.error('Failed to fetch interview result:', error);
          setInterviewResult({
            score: null,
            report: 'Failed to load interview report.',
            videoRecordings: []
          });
        } finally {
          setIsLoadingResult(false);
        }
      };
      fetchInterviewResult();
    }
  }, [isResultDialogOpen, selectedInvite]);

  // Handle resend invites
  const handleResendInvites = async () => {
    setIsResending(true);
    try {
      const response = await axiosInstance.post(`/ai-interview/send-reminder/${jobId}`);
      if (response.data && response.data.status === 'success') {
        const message = response.data.message || 'Reminders sent successfully!';
        setSubmitSuccess(message);
        setTimeout(() => setSubmitSuccess(''), 5000);
        setIsResendDialogOpen(false);
        // Refresh invites list
        fetchInvites();
      }
    } catch (error: any) {
      console.error('Failed to resend invites:', error);
      setSubmitError(error.response?.data?.message || 'Failed to send reminders');
      setTimeout(() => setSubmitError(''), 5000);
      setIsResendDialogOpen(false);
    } finally {
      setIsResending(false);
    }
  };

  // CSV Upload Functions
  const downloadSampleCSV = () => {
    setIsDownloadingSample(true);
    try {
      const headers = ['firstName', 'lastName', 'phone', 'email'];
      const sampleData = [
        ['John', 'Doe', '1234567890', 'john.doe@example.com'],
        ['Jane', 'Smith', '0987654321', 'jane.smith@example.com']
      ];
      
      // Escape CSV values (handle commas and quotes)
      const escapeCsvValue = (value: string): string => {
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };
      
      const csvContent = [
        headers.map(escapeCsvValue).join(','),
        ...sampleData.map(row => row.map(escapeCsvValue).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'sample_candidates.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setTimeout(() => setIsDownloadingSample(false), 500);
    }
  };

  const parseCSV = (csvText: string): Candidate[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      return [];
    }

    // Parse header row - handle quoted fields properly
    const parseCSVLine = (line: string): string[] => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      result.push(current.trim());
      return result;
    };

    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
    const expectedHeaders = ['firstname', 'lastname', 'phone', 'email'];
    
    // Find column indices
    const firstNameIdx = headers.findIndex(h => h === 'firstname' || h === 'first name');
    const lastNameIdx = headers.findIndex(h => h === 'lastname' || h === 'last name');
    const phoneIdx = headers.findIndex(h => h === 'phone' || h === 'mobile' || h === 'mobile number');
    const emailIdx = headers.findIndex(h => h === 'email' || h === 'email address');

    if (firstNameIdx === -1 || lastNameIdx === -1 || phoneIdx === -1 || emailIdx === -1) {
      throw new Error('CSV must contain columns: firstName, lastName, phone, email');
    }

    // Parse data rows
    const data: Candidate[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      
      if (values.length >= Math.max(firstNameIdx, lastNameIdx, phoneIdx, emailIdx) + 1) {
        const candidate: Candidate = {
          firstName: values[firstNameIdx]?.trim() || '',
          lastName: values[lastNameIdx]?.trim() || '',
          phone: values[phoneIdx]?.trim() || '',
          email: values[emailIdx]?.trim() || ''
        };
        
        // Only add if at least one field has data
        if (candidate.firstName || candidate.lastName || candidate.phone || candidate.email) {
          data.push(candidate);
        }
      }
    }

    return data;
  };

  const validateCSVData = (data: Candidate[]): { isValid: boolean; error: string } => {
    if (data.length === 0) {
      return { isValid: false, error: 'No valid data found in CSV file' };
    }

    if (data.length > 150) {
      return { isValid: false, error: `Maximum 150 candidates allowed. Found ${data.length} candidates.` };
    }

    // Check for missing fields
    const missingFields: number[] = [];
    data.forEach((candidate, index) => {
      if (!candidate.firstName.trim() || !candidate.lastName.trim() || !candidate.phone.trim() || !candidate.email.trim()) {
        missingFields.push(index + 1);
      }
    });

    if (missingFields.length > 0) {
      return { 
        isValid: false, 
        error: `Missing required fields in rows: ${missingFields.slice(0, 10).join(', ')}${missingFields.length > 10 ? '...' : ''}` 
      };
    }

    // Check for unique phone numbers
    const phoneNumbers = data.map(c => c.phone.trim());
    const phoneCounts = new Map<string, number[]>();
    
    phoneNumbers.forEach((phone, index) => {
      if (phone) {
        if (!phoneCounts.has(phone)) {
          phoneCounts.set(phone, []);
        }
        phoneCounts.get(phone)!.push(index + 1); // +1 because rows are 1-indexed for user display
      }
    });

    // Find duplicates
    const duplicateEntries: string[] = [];
    phoneCounts.forEach((rowNumbers, phone) => {
      if (rowNumbers.length > 1) {
        duplicateEntries.push(`Phone ${phone} appears in rows: ${rowNumbers.join(', ')}`);
      }
    });
    
    if (duplicateEntries.length > 0) {
      return { 
        isValid: false, 
        error: `Duplicate phone numbers found:\n${duplicateEntries.slice(0, 5).join('\n')}${duplicateEntries.length > 5 ? '\n...' : ''}` 
      };
    }

    // Validate email format
    const invalidEmails: number[] = [];

    data.forEach((candidate, index) => {
      if (
        !candidate.email ||
        !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
          candidate.email.trim()
        )
      ) {
        invalidEmails.push(index + 1);
      }
    });

    return { isValid: true, error: '' };
  };

  const processFile = (file: File) => {
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      setCsvError('Please upload a CSV file');
      return;
    }

    setCsvFile(file);
    setCsvError('');
    setCsvParsedData([]);
  };

  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      processFile(file);
    }
  };

  const handleCsvUpload = () => {
    if (!csvFile) {
      setCsvError('Please select a CSV file');
      return;
    }

    setIsParsingCsv(true);
    setCsvError('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        if (!csvText || csvText.trim().length === 0) {
          setCsvError('File is empty');
          setIsParsingCsv(false);
          return;
        }

        const parsed = parseCSV(csvText);
        const validation = validateCSVData(parsed);

        if (!validation.isValid) {
          setCsvError(validation.error);
          setIsParsingCsv(false);
          return;
        }

        setCsvParsedData(parsed);
        setCsvError('');
        
        // Close CSV dialog and open invite dialog with parsed data
        setIsCsvDialogOpen(false);
        setCandidates(parsed);
        setErrors(parsed.map(() => ({})));
        setIsDialogOpen(true);
        setSubmitSuccess('');
        setSubmitError('');
        setCsvFile(null);
      } catch (error: any) {
        setCsvError(error.message || 'Error parsing CSV file');
      } finally {
        setIsParsingCsv(false);
      }
    };

    reader.onerror = () => {
      setCsvError('Error reading file');
      setIsParsingCsv(false);
    };

    reader.readAsText(csvFile);
  };

  const handleCsvDialogClose = () => {
    setIsCsvDialogOpen(false);
    setCsvFile(null);
    setCsvError('');
    setCsvParsedData([]);
    setIsParsingCsv(false);
  };

  return (
    <div className="flex-1 overflow-auto p-3 sm:p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">AI Interview</h1>
          <p className="text-gray-600 mt-2">
            {jobId ? `Job ID: ${jobId}` : 'Conduct AI-powered interviews for candidates'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setIsCsvDialogOpen(true)}
            variant="outline"
            size="sm"
            className="border-[#EC6E51] text-[#EC6E51] hover:bg-[#EC6E51] hover:text-white"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload CSV
          </Button>
          
          <Button
            onClick={() => setIsResendDialogOpen(true)}
            variant="outline"
            size="sm"
            className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
          >
            <Send className="h-4 w-4 mr-2" />
            Resend Invites
          </Button>
          <Button
            onClick={() => {
              setIsDialogOpen(true);
              setErrors(candidates.map(() => ({})));
              setSubmitSuccess('');
              setSubmitError('');
            }}
            className="bg-[#EC6E51] hover:bg-[#d85a3f] text-white"
          >
            <Send className="h-4 w-4 mr-2" />
            Invite Candidate
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Interview Invites</h2>
          <div className="text-sm text-gray-500">
            Showing {invites.length} of {totalResults} invite{totalResults !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="w-full sm:w-48">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoadingInvites ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#EC6E51]" />
          </div>
        ) : invites.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No invites found. Click "Invite Candidate" to get started.</p>
          </div>
        ) : (
          <>
            {/* Card Layout for Small/Medium Devices */}
            <div className="space-y-4 max-h-[800px] min-h-[600px] overflow-y-auto pr-2 custom-scrollbar lg:hidden">
              {invites.map((invite) => (
                <div
                  key={invite._id}
                  className="border border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300 bg-white hover:border-[#EC6E51]/30"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4 pb-4 border-b border-gray-100">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 truncate">
                        {invite.firstName} {invite.lastName}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-2 truncate">
                        <span className="w-1 h-1 bg-gray-400 rounded-full flex-shrink-0"></span>
                        <span className="truncate">{invite.email}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(`${process.env.REACT_APP_AI_URL}/interview?sessionId=${invite.sessionId}`);
                            setCopiedId(invite._id);
                            setTimeout(() => setCopiedId(null), 2000);
                          } catch (err) {
                            console.error('Failed to copy:', err);
                          }
                        }}
                        className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                        title="Copy interview link"
                      >
                        {copiedId === invite._id ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                        )}
                      </button>
                      <span className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-semibold rounded-full uppercase tracking-wide whitespace-nowrap ${invite.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                          : invite.status === 'completed'
                            ? 'bg-green-100 text-green-800 border border-green-200'
                            : 'bg-gray-100 text-gray-800 border border-gray-200'
                        }`}>
                        {invite.status}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-4">
                    <div className="text-sm">
                      <span className="font-semibold text-gray-500 uppercase text-xs tracking-wide block mb-2">Phone</span>
                      <p className="text-gray-900 font-medium break-words">{invite.phone}</p>
                    </div>

                    <div className="text-sm">
                      <span className="font-semibold text-gray-500 uppercase text-xs tracking-wide block mb-2">Job Title</span>
                      <p className="text-gray-900 font-medium truncate" title={invite.jobId?.title || 'N/A'}>
                        {invite.jobId?.title || 'N/A'}
                      </p>
                    </div>

                    <div className="text-sm">
                      <span className="font-semibold text-gray-500 uppercase text-xs tracking-wide block mb-2">Invited On</span>
                      <p className="text-gray-900 font-medium">{new Date(invite.invitedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}</p>
                    </div>

                    <div className="text-sm">
                      <span className="font-semibold text-gray-500 uppercase text-xs tracking-wide block mb-2">Expires On</span>
                      <p className="text-gray-900 font-medium">{new Date(invite.expiresAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}</p>
                    </div>

                    {user.role === "ADMIN" && <div className="text-sm">
                      <span className="font-semibold text-gray-500 uppercase text-xs tracking-wide block mb-2">Invited By</span>
                      <p>{invite.invitedBy?.email}</p>
                    </div>
                    }


                  </div>

                  {invite.status === 'completed' && (
                    <div className="mt-3">
                      <button
                        onClick={() => {
                          setSelectedInvite(invite);
                          setIsResultDialogOpen(true);
                        }}
                        disabled={isLoadingResult && selectedInvite?._id === invite._id}
                        className="text-[#EC6E51] hover:text-[#d85a3f] hover:underline text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isLoadingResult && selectedInvite?._id === invite._id ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          'Show Result'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Table Layout for Large Devices */}
            <div className="hidden lg:block max-h-[800px] min-h-[600px] overflow-y-auto">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">Job Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">Invited On</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">Expires On</th>
                      {user.role === "ADMIN" && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">Invited By</th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invites.map((invite) => (
                      <tr key={invite._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {invite.firstName} {invite.lastName}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-700">{invite.email}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-700">{invite.phone}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-700">{invite.jobId?.title || 'N/A'}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full uppercase tracking-wide ${
                            invite.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                              : invite.status === 'completed'
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-gray-100 text-gray-800 border border-gray-200'
                          }`}>
                            {invite.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-700">
                            {new Date(invite.invitedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-700">
                            {new Date(invite.expiresAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </td>
                        {user.role === "ADMIN" && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-700">{invite.invitedBy?.email || 'N/A'}</div>
                          </td>
                        )}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(`${process.env.REACT_APP_AI_URL}/interview?sessionId=${invite.sessionId}`);
                                  setCopiedId(invite._id);
                                  setTimeout(() => setCopiedId(null), 2000);
                                } catch (err) {
                                  console.error('Failed to copy:', err);
                                }
                              }}
                              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                              title="Copy interview link"
                            >
                              {copiedId === invite._id ? (
                                <Check className="h-4 w-4 text-green-600" />
                              ) : (
                                <Copy className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                              )}
                            </button>
                            {invite.status === 'completed' && (
                              <button
                                onClick={() => {
                                  setSelectedInvite(invite);
                                  setIsResultDialogOpen(true);
                                }}
                                disabled={isLoadingResult && selectedInvite?._id === invite._id}
                                className="text-[#EC6E51] hover:text-[#d85a3f] hover:underline text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                              >
                                {isLoadingResult && selectedInvite?._id === invite._id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading...
                                  </>
                                ) : (
                                  'Show Result'
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1 || isLoadingInvites}
                  >
                    {isLoadingInvites ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ChevronLeft className="w-4 h-4" />
                    )}
                  </Button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages || isLoadingInvites}
                  >
                    {isLoadingInvites ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invite Candidates</DialogTitle>
            <DialogDescription>
              Add up to 5 candidates at a time. All fields are mandatory.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            {/* Header Row */}
            <div className="grid grid-cols-12 gap-3 mb-2 pb-2 border-b">
              <div className="col-span-1 text-sm font-medium text-gray-700">#</div>
              <div className="col-span-2 text-sm font-medium text-gray-700">
                First Name <span className="text-red-500">*</span>
              </div>
              <div className="col-span-2 text-sm font-medium text-gray-700">
                Last Name <span className="text-red-500">*</span>
              </div>
              <div className="col-span-2 text-sm font-medium text-gray-700">
                Phone <span className="text-red-500">*</span>
              </div>
              <div className="col-span-3 text-sm font-medium text-gray-700">
                Email <span className="text-red-500">*</span>
              </div>
              <div className="col-span-2 text-sm font-medium text-gray-700">Action</div>
            </div>

            {/* Candidate Rows */}
            <div className="space-y-4">
              {candidates.map((candidate, index) => (
                <div key={index}>
                  <div className="grid grid-cols-12 gap-3 items-start">
                    <div className="col-span-1 flex items-center justify-center text-sm font-medium text-gray-600 pt-2">
                      {index + 1}
                    </div>
                    <div className="col-span-2">
                      <Input
                        value={candidate.firstName}
                        onChange={(e) => handleCandidateChange(index, 'firstName', e.target.value)}
                        placeholder="First name"
                        required
                        className={errors[index]?.firstName ? 'border-red-500' : ''}
                      />
                      {errors[index]?.firstName && (
                        <p className="text-xs text-red-500 mt-1">{errors[index].firstName}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Input
                        value={candidate.lastName}
                        onChange={(e) => handleCandidateChange(index, 'lastName', e.target.value)}
                        placeholder="Last name"
                        required
                        className={errors[index]?.lastName ? 'border-red-500' : ''}
                      />
                      {errors[index]?.lastName && (
                        <p className="text-xs text-red-500 mt-1">{errors[index].lastName}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Input
                        value={candidate.phone}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/\D/g, '');
                          handleCandidateChange(index, 'phone', numericValue);
                        }}
                        placeholder="Phone number"
                        type="tel"
                        maxLength={10}
                        minLength={10}
                        required
                        className={errors[index]?.phone ? 'border-red-500' : ''}
                      />
                      {errors[index]?.phone && (
                        <p className="text-xs text-red-500 mt-1">{errors[index].phone}</p>
                      )}
                    </div>
                    <div className="col-span-3">
                      <Input
                        value={candidate.email}
                        onChange={(e) => handleCandidateChange(index, 'email', e.target.value)}
                        placeholder="Email address"
                        type="email"
                        required
                        className={errors[index]?.email ? 'border-red-500' : ''}
                      />
                      {errors[index]?.email && (
                        <p className="text-xs text-red-500 mt-1">{errors[index].email}</p>
                      )}
                    </div>
                    <div className="col-span-2 flex items-center gap-2 pt-2">
                      {candidates.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveCandidate(index)}
                          className="h-8 w-8 p-0 text-gray-500 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {submitError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}

            {submitSuccess && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-600">{submitSuccess}</p>
              </div>
            )}

            {candidates.length < 5 && (
              <div className="mt-4">
                <Button
                  variant="outline"
                  onClick={handleAddCandidate}
                  className="w-full border-dashed"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another Candidate
                </Button>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleDialogClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-[#EC6E51] hover:bg-[#d85a3f] text-white disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Inviting...
                  </>
                ) : (
                  `Invite ${candidates.length} Candidate${candidates.length > 1 ? 's' : ''}`
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Interview Result Dialog */}
      <Dialog
        open={isResultDialogOpen}
        onOpenChange={(open) => {
          setIsResultDialogOpen(open);
          if (!open) {
            setSelectedInvite(null);
            setInterviewResult(null);
            setIsLoadingResult(false);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedInvite && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle>Interview Result</DialogTitle>
                    <DialogDescription>
                      Detailed results for {selectedInvite.firstName} {selectedInvite.lastName}
                    </DialogDescription>
                  </div>
                  <button
                    onClick={() => {
                      setIsResultDialogOpen(false);
                      setSelectedInvite(null);
                      setInterviewResult(null);
                      setIsLoadingResult(false);
                    }}
                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </button>
                </div>
              </DialogHeader>


              {/* Candidate and Interview Details */}
              {isLoadingResult ? (
                <div className="flex items-center justify-center py-12 mt-4">
                  <Loader2 className="h-8 w-8 animate-spin text-[#EC6E51]" />
                </div>
              ) : (
                <div className="mt-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    {/* Header with Score and Name */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pb-3 border-b border-gray-200">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-0.5">
                          {selectedInvite.firstName} {selectedInvite.lastName}
                        </h3>
                        <p className="text-xs text-gray-600">{selectedInvite.email}</p>
                      </div>
                      <div className="flex items-center gap-2 bg-[#EC6E51]/10 rounded-lg px-4 py-2 border border-[#EC6E51]/20">
                        <div>
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-0.5">Score</p>
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-bold text-[#EC6E51]">
                              {interviewResult?.report?.score ? (typeof interviewResult.report.score === 'number' ? interviewResult.report.score.toFixed(1) : interviewResult.report.score) : 'N/A'}
                            </span>
                            <span className="text-sm text-gray-500 font-medium">/ 10</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                          <span className="w-1 h-1 bg-[#EC6E51] rounded-full"></span>
                          Job Title
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {selectedInvite.jobId?.title || 'N/A'}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                          <span className="w-1 h-1 bg-[#EC6E51] rounded-full"></span>
                          Interview Date
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {new Date(selectedInvite.invitedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                          <span className="w-1 h-1 bg-[#EC6E51] rounded-full"></span>
                          Session ID
                        </p>
                        <p className="text-xs font-mono text-gray-700 break-all bg-gray-50 px-2 py-1 rounded border border-gray-200">
                          {selectedInvite.sessionId}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tabs */}
              {!isLoadingResult && (
                <Tabs defaultValue="report" className="mt-6">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="report" className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Report
                    </TabsTrigger>
                    <TabsTrigger value="recordings" className="flex items-center gap-2">
                      <Video className="h-4 w-4" />
                      Video Recordings
                    </TabsTrigger>
                    <TabsTrigger value="transcripts" className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Transcripts
                    </TabsTrigger>
                  </TabsList>

                  {/* Report Tab */}
                  <TabsContent value="report" className="mt-4">
                    {isLoadingResult ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-[#EC6E51]" />
                      </div>
                    ) : (
                      <div className="p-4 bg-white border border-gray-200 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Interview Report</h3>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* Recommendations */}
                          {interviewResult?.report?.recommendations ? (
                            <div className="lg:col-span-2 bg-green-50 border border-green-200 rounded-lg p-4">
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                Recommendations
                              </h4>
                              <ul className="space-y-2">
                                {interviewResult?.report?.recommendations?.map(
                                  (point: string, index: number) => (
                                    <li
                                      key={`${point}-${index}`}
                                      className="text-sm text-gray-700 pl-4 relative before:content-['✓'] before:absolute before:left-0 before:text-green-600 before:font-bold"
                                    >
                                      {point}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          ) : null}

                          {/* Technical Assessment */}
                          {interviewResult?.report?.technical ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                Technical Assessment
                              </h4>
                              <div className="space-y-3">
                                {interviewResult?.report?.technical?.positives?.length > 0 && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Positive Points</h5>
                                    <ul className="space-y-1.5">
                                      {interviewResult?.report?.technical?.positives?.map(
                                        (point: string, index: number) => (
                                          <li
                                            key={`tech-pos-${index}`}
                                            className="text-xs text-gray-700 pl-4 relative before:content-['+'] before:absolute before:left-0 before:text-green-600 before:font-bold"
                                          >
                                            {point}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                                {interviewResult?.report?.technical?.negatives?.length > 0 && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Areas for Improvement</h5>
                                    <ul className="space-y-1.5">
                                      {interviewResult?.report?.technical?.negatives?.map(
                                        (point: string, index: number) => (
                                          <li
                                            key={`tech-neg-${index}`}
                                            className="text-xs text-gray-700 pl-4 relative before:content-['-'] before:absolute before:left-0 before:text-red-600 before:font-bold"
                                          >
                                            {point}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : null}

                          {/* Communication */}
                          {interviewResult?.report?.communication ? (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                                Communication
                              </h4>
                              <div className="space-y-3">
                                {interviewResult?.report?.communication?.positives?.length > 0 && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Positive Points</h5>
                                    <ul className="space-y-1.5">
                                      {interviewResult?.report?.communication?.positives?.map(
                                        (point: string, index: number) => (
                                          <li
                                            key={`comm-pos-${index}`}
                                            className="text-xs text-gray-700 pl-4 relative before:content-['+'] before:absolute before:left-0 before:text-green-600 before:font-bold"
                                          >
                                            {point}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                                {interviewResult?.report?.communication?.negatives?.length > 0 && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Areas for Improvement</h5>
                                    <ul className="space-y-1.5">
                                      {interviewResult?.report?.communication?.negatives?.map(
                                        (point: string, index: number) => (
                                          <li
                                            key={`comm-neg-${index}`}
                                            className="text-xs text-gray-700 pl-4 relative before:content-['-'] before:absolute before:left-0 before:text-red-600 before:font-bold"
                                          >
                                            {point}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : null}

                          {!interviewResult?.report?.recommendations && !interviewResult?.report?.technical && !interviewResult?.report?.communication && (
                            <div className="lg:col-span-2 text-center py-8">
                              {/* <p className="text-gray-500">No report available yet.</p> */}
                              <p className="text-gray-500">{JSON.stringify(interviewResult,null,2)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {/* Video Recordings Tab */}
                  <TabsContent value="recordings" className="mt-4">
                    {isLoadingResult ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-[#EC6E51]" />
                      </div>
                    ) : (
                      <div className="p-4 bg-white border border-gray-200 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Video Recordings</h3>
                        {true ? (
                            <video 
                              src={interviewResult?.finalRecording} 
                              controls 
                              className="w-full rounded-lg"
                            />
                        ) : (
                          <p className="text-gray-500 text-center py-8">No video recordings available.</p>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  {/* Transcripts Tab */}
                  <TabsContent value="transcripts" className="mt-4">
                    {isLoadingResult ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-[#EC6E51]" />
                      </div>
                    ) : (
                      <div className="p-4 bg-white border border-gray-200 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Interview Transcripts</h3>
                        {interviewResult?.transcripts && interviewResult?.transcripts?.length > 0 ? (
                          <div className="space-y-4">
                            {Array.isArray(interviewResult.transcripts) ? (
                              interviewResult?.transcripts?.map((entry: any, index: number) => (
                                <div
                                  key={index}
                                  className="p-4 bg-gray-50 border border-gray-200 rounded-lg"
                                >
                                  {entry.question && (
                                    <div className="font-semibold text-gray-900 mb-2">
                                      {entry.question}
                                    </div>
                                  )}
                                  {entry.answer && (
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                      {entry.answer}
                                    </p>
                                  )}
                                  {entry.timestamp && (
                                    <p className="text-xs text-gray-500 mt-2">
                                      {new Date(entry.timestamp).toLocaleTimeString()}
                                    </p>
                                  )}
                                </div>
                              ))
                            ) : typeof interviewResult.transcripts ? (
                              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {interviewResult.transcripts}
                                </p>
                              </div>
                            ) : (
                              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {JSON.stringify(interviewResult.transcripts, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-8">No transcripts available.</p>
                        )}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={isCsvDialogOpen} onOpenChange={handleCsvDialogClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Candidates via CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with candidate information. Maximum 150 candidates per upload.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            {/* Download Sample CSV Button */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <p className="text-sm font-medium text-gray-900">Need a sample file?</p>
                <p className="text-xs text-gray-500 mt-1">Download a sample CSV template with the correct format</p>
              </div>
              <Button
                onClick={downloadSampleCSV}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                disabled={isDownloadingSample}
              >
                {isDownloadingSample ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Download Sample CSV
                  </>
                )}
              </Button>
            </div>

            {/* File Upload Area */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Select CSV File</label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isDragOver
                    ? 'border-[#EC6E51] bg-[#EC6E51]/5'
                    : 'border-gray-300 hover:border-[#EC6E51]'
                }`}
              >
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvFileChange}
                  className="hidden"
                  id="csv-upload-input"
                />
                <label
                  htmlFor="csv-upload-input"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload className={`h-8 w-8 ${isDragOver ? 'text-[#EC6E51]' : 'text-gray-400'}`} />
                  <div>
                    <span className="text-sm font-medium text-[#EC6E51]">Click to upload</span>
                    <span className="text-sm text-gray-500"> or drag and drop</span>
                  </div>
                  <p className="text-xs text-gray-500">CSV file only (Max 150 candidates)</p>
                </label>
                {csvFile && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-800 font-medium">
                      Selected: {csvFile.name}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      {(csvFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* CSV Format Requirements */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-medium text-blue-900 mb-2">CSV Format Requirements:</p>
              <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                <li>Required columns: firstName, lastName, phone, email</li>
                <li>All fields are mandatory</li>
                <li>Phone numbers must be unique</li>
                <li>Email addresses must be valid</li>
                <li>Maximum 150 candidates per file</li>
              </ul>
            </div>

            {/* Error Display */}
            {csvError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 whitespace-pre-line">{csvError}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleCsvDialogClose}
                disabled={isParsingCsv}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCsvUpload}
                disabled={!csvFile || isParsingCsv}
                className="bg-[#EC6E51] hover:bg-[#d85a3f] text-white disabled:opacity-50"
              >
                {isParsingCsv ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload & Parse
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resend Invites Confirmation Dialog */}
      <Dialog open={isResendDialogOpen} onOpenChange={setIsResendDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resend Interview Invitations</DialogTitle>
            <DialogDescription>
              This will send reminder emails and WhatsApp messages to all candidates with pending invitations. 
              The invitation expiry date will be extended by 5 days from now. Are you sure you want to proceed?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsResendDialogOpen(false)}
              disabled={isResending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResendInvites}
              disabled={isResending}
              className="bg-[#EC6E51] hover:bg-[#d85a3f] text-white"
            >
              {isResending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Reminders
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIInterview;


type VideoPlayerProps = {
  videoUrls: string[];        // 👈 array prop
  chunkDuration?: number;     // optional, defaults to 10s
};

const VideoPlayer: React.FC<VideoPlayerProps> = memo(
  ({ videoUrls, chunkDuration = 10 }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const preloadRef = useRef<HTMLVideoElement | null>(null);

    const [index, setIndex] = useState<number>(0);
    const [playing, setPlaying] = useState<boolean>(false);
    const [globalTime, setGlobalTime] = useState<number>(0);

    const TOTAL_DURATION = videoUrls.length * chunkDuration;

    /* ---------------- LOAD CURRENT CHUNK (NO FLICKER) ---------------- */
    useEffect(() => {
      const video = videoRef.current;
      if (!video || !videoUrls[index]) return;

      const nextSrc = videoUrls[index];

      // Prevent unnecessary reloads
      if (video.src && video.src.includes(nextSrc)) return;

      video.src = nextSrc;
      video.currentTime = 0;

      if (playing) {
        video.play().catch(() => {});
      }
    }, [index, playing, videoUrls]);

    /* ---------------- PRELOAD NEXT CHUNK ---------------- */
    useEffect(() => {
      const preload = preloadRef.current;
      const nextIndex = index + 1;

      if (!preload || !videoUrls[nextIndex]) return;

      preload.src = videoUrls[nextIndex];
      preload.preload = "auto";
      preload.load();
    }, [index, videoUrls]);

    /* ---------------- GLOBAL TIME TRACKING ---------------- */
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      const onTimeUpdate = () => {
        setGlobalTime(
          index * chunkDuration + video.currentTime
        );
      };

      video.addEventListener("timeupdate", onTimeUpdate);
      return () => {
        video.removeEventListener("timeupdate", onTimeUpdate);
      };
    }, [index, chunkDuration]);

    /* ---------------- AUTO NEXT CHUNK ---------------- */
    const handleEnded = (): void => {
      if (index < videoUrls.length - 1) {
        setIndex((i) => i + 1);
      } else {
        setPlaying(false);
      }
    };

    /* ---------------- PLAY / PAUSE ---------------- */
    const togglePlay = (): void => {
      const video = videoRef.current;
      if (!video) return;

      if (playing) {
        video.pause();
      } else {
        video.play().catch(() => {});
      }

      setPlaying((p) => !p);
    };

    /* ---------------- GLOBAL SEEK ---------------- */
    const handleSeek = (
      e: React.ChangeEvent<HTMLInputElement>
    ): void => {
      const target = Number(e.target.value);
      const newIndex = Math.floor(target / chunkDuration);
      const offset = target % chunkDuration;

      if (newIndex !== index) {
        setIndex(newIndex);
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.currentTime = offset;
          }
        }, 60);
      } else if (videoRef.current) {
        videoRef.current.currentTime = offset;
      }

      setGlobalTime(target);
    };

    if (!videoUrls.length) return null;

    return (
      <div style={{ maxWidth: 900, padding: 20 }}>
        <video
          ref={videoRef}
          width="100%"
          height="400"
          style={{ background: "black" }}
          onEnded={handleEnded}
        />

        <div style={{ marginTop: 10 }}>
          <button onClick={togglePlay}>
            {playing ? "Pause" : "Play"}
          </button>
        </div>

        <input
          type="range"
          min={0}
          max={TOTAL_DURATION}
          value={globalTime}
          onChange={handleSeek}
          style={{ width: "100%", marginTop: 10 }}
        />

        <div style={{ textAlign: "center", marginTop: 6 }}>
          {Math.floor(globalTime)}s / {TOTAL_DURATION}s
        </div>

        {/* hidden preload video */}
        <video
          ref={preloadRef}
          style={{ display: "none" }}
          muted
        />
      </div>
    );
  }
);

